import React from "react";
import { Mail, ShieldCheck, UserRound } from "lucide-react";
import { cn } from "@/lib/utils";
import { HUB_ROLE, type HubRoleName } from "@/lib/auth/roles";

interface UserOptionPreviewProps {
  /** Primary label — the user's full name (or email/id fallback). */
  label: string;
  email?: string;
  /** Human-readable role label (e.g. "Admin"), already resolved by the caller. */
  role?: string;
  /** When explicitly false, render an "Inactive" badge. Undefined = unknown, no badge. */
  isActive?: boolean;
  /** "responsive" (default) collapses metadata to a row at >=md. "vertical" stays stacked. */
  layout?: "responsive" | "vertical";
}

/** Map a raw Hub role name to a short human label. Returns undefined for anything unknown
 * (e.g. a raw role_id uuid) so the caller only ever passes a display-worthy string. */
export function humanizeHubRole(role: string | null | undefined): string | undefined {
  switch (role as HubRoleName) {
    case HUB_ROLE.ADMIN:
      return "Admin";
    case HUB_ROLE.CRM:
      return "CRM";
    case HUB_ROLE.EMPLOYEE:
      return "Employee";
    case HUB_ROLE.CLIENT:
      return "Client";
    default:
      return undefined;
  }
}

/**
 * Rich list-item preview for a Hub user in a searchable dropdown — mirrors
 * {@link ../ContactOptionPreview.ContactOptionPreview} so user selection reads
 * the same as contact selection: bold name + an icon-prefixed metadata subheader
 * (email, role) instead of a bare name.
 */
export function UserOptionPreview({
  label,
  email,
  role,
  isActive,
  layout = "responsive",
}: UserOptionPreviewProps): React.ReactNode {
  const hasMetadata = Boolean(email || role);
  const metadataWrapperClass =
    layout === "vertical"
      ? "flex flex-col gap-1 text-xs text-muted-foreground"
      : "grid grid-cols-1 gap-1 text-xs text-muted-foreground md:flex md:items-center md:gap-3";

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex items-center gap-2">
        <UserRound className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
        <span className="font-medium truncate">{label}</span>
        {isActive === false && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-card-100 text-gray-800 dark:text-gray-200">
            Inactive
          </span>
        )}
      </div>
      {hasMetadata && (
        <div className={cn(metadataWrapperClass, "pl-5")}>
          {email && (
            <div className="flex min-w-0 items-center gap-1 md:flex-1">
              <Mail className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{email}</span>
            </div>
          )}
          {role && (
            <div className="flex min-w-0 items-center gap-1 md:flex-1">
              <ShieldCheck className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{role}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
